import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { QuotaError, getCategories, getSourceMap, getSubcategories } from './api/content';
import { getMyStates, setSaved, setVote, type MyState } from './api/user';
import { useAuth } from './auth';
import { t, useUiLang } from './i18n';
import { categoryColor, categoryLabel } from './taxonomy';
import type { Category } from './types';
import { ui } from './ui';

export function useCategories() {
  return useQuery({ queryKey: ['categories'], queryFn: getCategories, staleTime: Infinity });
}

export function useSubcategories() {
  return useQuery({ queryKey: ['subcategories'], queryFn: getSubcategories, staleTime: Infinity });
}

export function useSourceMap() {
  return useQuery({ queryKey: ['sourceMap'], queryFn: getSourceMap, staleTime: 30 * 60_000 });
}

/** Kategori kimliğinden ad + renk çözümleyici. */
export function useCategoryLookup() {
  const lang = useUiLang();
  const { data } = useCategories();
  return useMemo(() => {
    const byId = new Map<string, Category>((data ?? []).map((c) => [c.id, c]));
    const bySlug = new Map<string, Category>((data ?? []).map((c) => [c.slug, c]));
    return {
      byId,
      bySlug,
      name: (id: string | null | undefined) => {
        const c = id ? byId.get(id) : undefined;
        return c ? categoryLabel(lang, c.slug, c.name) : '';
      },
      color: (id: string | null | undefined) => categoryColor(id ? byId.get(id)?.slug : null),
      slug: (id: string | null | undefined) => (id ? byId.get(id)?.slug ?? null : null),
    };
  }, [data, lang]);
}

/** Giriş gerektiren eylemler için: oturum yoksa giriş sayfasına yönlendirir. */
export function useRequireAuth() {
  const { user } = useAuth();
  const nav = useNavigate();
  return useCallback(
    (fn: (uid: string) => void) => {
      if (!user) {
        nav(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }
      fn(user.id);
    },
    [user, nav]
  );
}

/** Edge fn kota/premium hatalarını kullanıcıya gösterir. */
export function handleActionError(e: unknown) {
  if (e instanceof QuotaError) {
    if (e.code === 'premium_required') ui.openPremium();
    else ui.toast(t('quota_exceeded'));
    return;
  }
  ui.toast(t('err_generic'));
}

/** Oy + kaydet durumu (iyimser güncelleme). */
export function useMyStates(ids: string[]) {
  const { user, premium } = useAuth();
  const qc = useQueryClient();
  const key = ['myStates', user?.id, ids.join(',')];
  const q = useQuery({
    queryKey: key,
    enabled: !!user && ids.length > 0,
    queryFn: () => getMyStates(user!.id, ids),
  });
  const require = useRequireAuth();

  const patch = (id: string, p: Partial<MyState>) => {
    qc.setQueryData<Map<string, MyState>>(key, (prev) => {
      const next = new Map(prev ?? []);
      next.set(id, { ...(next.get(id) ?? { vote: 0, saved: false }), ...p });
      return next;
    });
  };

  return {
    get: (id: string): MyState => q.data?.get(id) ?? { vote: 0, saved: false },
    vote: (id: string, value: -1 | 1) =>
      require((uid) => {
        const cur = q.data?.get(id)?.vote ?? 0;
        const next = cur === value ? 0 : value;
        patch(id, { vote: next });
        setVote(uid, id, next as -1 | 0 | 1).catch((e) => {
          patch(id, { vote: cur });
          handleActionError(e);
        });
      }),
    toggleSave: (id: string) =>
      require((uid) => {
        if (!premium) {
          ui.openPremium('save');
          return;
        }
        const cur = q.data?.get(id)?.saved ?? false;
        patch(id, { saved: !cur });
        setSaved(uid, id, !cur)
          .then(() => {
            ui.toast(t(!cur ? 'story_saved' : 'story_save'));
            void qc.invalidateQueries({ queryKey: ['saved'] });
          })
          .catch((e) => {
            patch(id, { saved: cur });
            handleActionError(e);
          });
      }),
  };
}

export function useScrolled(threshold = 4) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > threshold);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [threshold]);
  return scrolled;
}

export function useDocumentTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title ? `${title} · Brifing` : 'Brifing';
  }, [title]);
}

export function isTypingTarget(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n) return false;
  return n.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(n.tagName);
}
